import { useState } from "react";
import { ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";

import { useMarkPaid } from "../hooks/useEmployeeAgreements";
import type { EmployeeBillingPeriod } from "../types";

interface Props {
  period: EmployeeBillingPeriod;
  canMarkPaid?: boolean;
  canUploadInvoice?: boolean;
  onUploadInvoice?: (period: EmployeeBillingPeriod) => void;
}

export function BillingPeriodRow({
  period,
  canMarkPaid = false,
  canUploadInvoice = false,
  onUploadInvoice,
}: Props) {
  const [open, setOpen] = useState(false);
  const { mutate: markPaid, isPending } = useMarkPaid();
  const employee = period.agreement_detail.employee_detail.full_name;
  const isB2B = period.agreement_detail.is_b2b;
  const isCeo = !!useAuthStore((s) => s.user)?.is_ceo;

  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-bg-hover)]"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <div className="flex-1 text-left">
          <div className="text-sm">
            <strong>{employee}</strong> — {formatDate(period.period_start)} ↔{" "}
            {formatDate(period.period_end)}
          </div>
          <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
            {period.items.length} pozycji ({period.free_items_count} darmowych
            · {period.paid_items_count} płatnych)
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-mono">{period.total_amount} PLN</div>
          {period.paid_at ? (
            <Badge color="green">Opłacone</Badge>
          ) : period.invoice_uploaded_at ? (
            <Badge color="blue">Faktura wgrana</Badge>
          ) : (
            <Badge color="gray">Wygenerowane</Badge>
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-[var(--color-border)]">
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <th className="text-left py-1">Typ podpisu</th>
                <th className="text-left py-1">Dokument</th>
                <th className="text-right py-1">Ilość</th>
                {isCeo && <th className="text-right py-1">Stawka</th>}
                <th className="text-right py-1">Kwota</th>
              </tr>
            </thead>
            <tbody>
              {period.items.map((it) => (
                <tr key={it.id}>
                  <td className="py-1">{it.signature_type_name}</td>
                  <td className="py-1 text-[var(--color-text-muted)]">
                    {it.document_title}
                  </td>
                  <td className="py-1 text-right">{it.quantity}</td>
                  {isCeo && (
                    <td className="py-1 text-right">
                      {it.is_free ? "Darmowy" : `${it.rate} PLN`}
                    </td>
                  )}
                  <td className="py-1 text-right">{it.line_total}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end gap-2 mt-3">
            {canUploadInvoice && isB2B && !period.paid_at && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onUploadInvoice?.(period)}
              >
                {period.invoice_uploaded_at
                  ? "Wymień fakturę"
                  : "Wgraj fakturę"}
              </Button>
            )}
            {canMarkPaid && isB2B && !period.paid_at && (
              <Button
                size="sm"
                onClick={() => markPaid(period.id)}
                disabled={isPending}
              >
                <CheckCircle2 size={14} /> Oznacz jako opłacone
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
